import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { registerDestructor } from '@ember/destroyable';
import { task, timeout } from 'ember-concurrency';
import { removeLayer, pushLayer, isNuovaDocument, isTopLayer, hasOpenLayerInside } from '../lib/nv-layer-stack.js';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{this.updateSlider}}\n\n{{! `...attributes` on the panel container, matching BbModal and BbModalModern.\n    Without it every attribute a consumer put on `<BbPanel>` — a `data-test-*`\n    hook included — reached no element, so nothing behind a slide panel could be\n    driven by a test. 105 templates invoke this component, and two of them had\n    already worked around the gap by hooking an inner element instead. }}\n{{! Dialog semantics + focus (overlays.md §2) only while open: the panel root stays mounted when closed, so\n    role / aria-modal are gated on @showModal and nv-dialog-focus gets enabled=@showModal (nuova only: frame\n    focused on open, Tab trap, focus back to the opener). The header wrapper exists only while open: it\n    registers the panel in the open-order layer stack (nuova Escape + Back button). }}\n<div\n  class=\'slide-panel {{this.sideClass}} {{this.showSliderClass}} {{this.hideSliderClass}}\'\n  style=\'{{css-style \"z-index\" this.zIndexStyle}}\'\n  role={{if @showModal \'dialog\'}}\n  aria-modal={{if @showModal \'true\'}}\n  aria-labelledby={{if @showModal this.titleId}}\n  ...attributes\n  {{nv-dialog-focus initial=\'self\' enabled=(if @showModal true false)}}\n>\n  {{#if @showModal}}\n    <div class=\'header-container\' {{did-insert this.layerOpened}} {{will-destroy this.layerClosed}}>\n      <div class=\'header main-header\'>\n        <div class=\'grid-x grid-padding-x detail-item-button\'>\n          <div class=\'cell auto\'>\n            <h4 id={{this.titleId}}>\n              {{@title}}\n            </h4>\n          </div>\n          <div class=\'cell shrink text-right\'>\n            <button\n              type=\'button\'\n              class=\'hdr-btn {{if this.closeTask.isRunning \"loading\"}}\'\n              disabled={{or this.closeTask.isRunning this.isBusy}}\n              aria-label={{t \'close\'}}\n              data-test-bb-panel-close\n              {{on \'click\' (perform this.closeTask)}}\n            >\n              <FaIcon @icon=\'times\' aria-hidden=\'true\' />\n              <EmberTooltip @popperContainer=\'body\'>\n                {{t \'close\'}}\n              </EmberTooltip>\n            </button>\n          </div>\n        </div>\n      </div>\n    </div>\n    <main class=\'main-content\'>\n      {{yield}}\n    </main>\n    <div\n      class=\'resize-separator {{this.sideClass}} {{this.showSliderClass}} {{this.hideSliderClass}}\'\n    ></div>\n  {{/if}}\n</div>");

var _dec, _dec2, _class, _descriptor, _descriptor2;
const OUTSIDE_POPUPS = '.ember-basic-dropdown-content, .ember-power-select-dropdown, .pika-single:not(.is-hidden)';
const ANIMATION_DELAY_SHORT = 50;
const ANIMATION_DELAY_MEDIUM = 150;

/**
 * CSS class constants
 * @constant {string}
 */
const CLASS_OPEN = 'open';
const CLASS_HIDE = 'hide';
const CLASS_HIDE_HEADER = 'hide-header';

/**
 * Default z-index value for the slide panel
 * @constant {string}
 */
const DEFAULT_Z_INDEX = '10';

/**
 * SlidePanel Component
 *
 * A sliding panel component that animates in/out from the side.
 * Manages body scroll and header visibility during transitions.
 *
 * @class BbPanelComponent
 * @extends Component
 *
 * @arg {boolean} showModal - Controls panel visibility
 * @arg {Function} closeModal - Callback invoked when panel closes
 * @arg {string} [zIndex='10'] - Custom z-index for the panel
 * @arg {string} [side='right'] - Edge the panel slides in from ('right' | 'left')
 */
let BbPanelComponent = (_dec = task({
  restartable: true
}), _dec2 = task({
  restartable: true
}), _class = class BbPanelComponent extends Component {
  constructor() {
    super(...arguments);
    // «UI nuova»: Escape closes the panel when it is the last opened layer. Document-level, like BbModalModern's
    // fallback listener: the focus can be on the frame, a field, or <body> after a click on the page.
    /**
     * Current visibility class for the slider ('open' when visible)
     * @type {string|null}
     */
    _initializerDefineProperty(this, "showSliderClass", _descriptor, this);
    /**
     * Current hide class for the slider ('hide' when hidden)
     * @type {string|null}
     */
    _initializerDefineProperty(this, "hideSliderClass", _descriptor2, this);
    // id of the title <h4>, the open panel's aria-labelledby target
    this.titleId = `bb-panel-title-${guidFor(this)}`;
    // the open panel's root (.slide-panel), set while it is a layer of lib/nv-layer-stack
    this.layerElement = null;
    const onKey = event => this.handleDocumentKeydown(event);
    document.addEventListener('keydown', onKey);
    registerDestructor(this, () => {
      document.removeEventListener('keydown', onKey);
      removeLayer(this);
    });
  }

  // Open-order stack: the header wrapper is rendered only while @showModal — bookkeeping only (read in nuova).
  layerOpened(element) {
    this.layerElement = element.closest('.slide-panel') ?? element.parentElement;
    pushLayer(this, this.layerElement, () => this.closeFromKeyboard()); // Back = ✕ (same lock)
  }
  layerClosed() {
    this.layerElement = null;
    removeLayer(this);
  }
  handleDocumentKeydown(event) {
    if (event?.key !== 'Escape' || event.defaultPrevented || !isNuovaDocument() || !this.layerElement) return;
    if (!isTopLayer(this)) return;
    // an open pane / combobox inside the panel takes Escape first (same rule as the modals)
    if (hasOpenLayerInside(this.layerElement)) return;
    // popups rendered OUTSIDE the panel (power-select / basic-dropdown wormhole, Pikaday) close first: the same
    // keystroke must not also unmount the panel and whatever was typed in it
    if (document.querySelector(OUTSIDE_POPUPS)) return;
    this.closeFromKeyboard();
  }

  // The ✕'s own lock: disabled while closing or busy (see isBusy) — Escape / Back honour it the same way.
  closeFromKeyboard() {
    if (this.isBusy || this.closeTask.isRunning || !this.args.showModal) return;
    this.closeTask.perform();
  }

  /**
   * Returns the z-index style value
   * @returns {string} The z-index value
   */
  get zIndexStyle() {
    return this.args.zIndex ?? DEFAULT_Z_INDEX;
  }

  /**
   * Edge the panel is anchored to. Defaults to the historical right side, so
   * the 100+ existing consumers keep sliding in from the right.
   * @returns {string} 'left' or 'right'
   */
  get sideClass() {
    return this.args.side === 'left' ? 'left' : 'right';
  }

  /**
   * Reactive getter that triggers panel open/close based on showModal arg.
   * Note: This getter has side effects by design for template reactivity.
   * @returns {null}
   */
  get updateSlider() {
    const {
      showModal
    } = this.args;
    if (showModal) {
      this.upTask.perform();
    } else if (this.showSliderClass === CLASS_OPEN) {
      this.closeTask.perform();
    }
    return null;
  }

  /**
   * Checks if the panel is currently open
   * @returns {boolean}
   */
  get isOpen() {
    return this.showSliderClass === CLASS_OPEN;
  }

  /**
   * When truthy, the ✕ close button is disabled. Pass
   * `@busy={{this.someSaveTask.isRunning}}` so the panel — and the detail
   * subtree it yields — stay mounted for the WHOLE task. Closing the panel
   * flips the parent's `@showModal`, which unmounts `{{yield}}`; a save task
   * running in that subtree is then auto-cancelled by ember-concurrency at its
   * next yield, silently dropping the rest of the write. Only the user-initiated
   * ✕ is gated; the parent's own programmatic close (via `@showModal`) is left
   * intact.
   * @returns {boolean}
   */
  get isBusy() {
    return this.args.busy ?? false;
  }

  /**
   * Safely adds a class to the document body
   * @param {string} className - The class to add
   * @private
   */
  _addBodyClass(className) {
    document.body?.classList.add(className);
  }

  /**
   * Safely removes a class from the document body
   * @param {string} className - The class to remove
   * @private
   */
  _removeBodyClass(className) {
    document.body?.classList.remove(className);
  }

  /**
   * Safely invokes the closeModal callback if provided
   * @private
   */
  _invokeCloseModal() {
    if (typeof this.args.closeModal === 'function') {
      this.args.closeModal();
    }
  }

  /**
   * Ensures hide-header class is always removed when component is destroyed,
   * even if the parent tears it down via {{#if}} before closeTask can run.
   */
  willDestroy() {
    super.willDestroy(...arguments);
    removeLayer(this);
    this._removeBodyClass(CLASS_HIDE_HEADER);
    // Cancella timeout di animazione per evitare update su component distrutto
    this.upTask?.cancelAll?.();
    this.closeTask?.cancelAll?.();
  }

  /**
   * Task to open the slide panel with animation
   * @task
   */
  *upTask() {
    yield timeout(ANIMATION_DELAY_SHORT);
    if (this.isDestroying || this.isDestroyed) return;
    if (!this.args.showModal) {
      this.closeTask.perform();
      return;
    }
    this.hideSliderClass = null;
    yield timeout(ANIMATION_DELAY_MEDIUM);
    if (this.isDestroying || this.isDestroyed) return;
    this._addBodyClass(CLASS_HIDE_HEADER);
    this.showSliderClass = CLASS_OPEN;
  }

  /**
   * Task to close the slide panel with animation
   * @task
   */
  *closeTask() {
    yield timeout(ANIMATION_DELAY_SHORT);
    if (this.isDestroying || this.isDestroyed) return;
    this.showSliderClass = null;
    this._removeBodyClass(CLASS_HIDE_HEADER);
    yield timeout(ANIMATION_DELAY_MEDIUM);
    if (this.isDestroying || this.isDestroyed) return;
    this.hideSliderClass = CLASS_HIDE;
    yield timeout(ANIMATION_DELAY_MEDIUM);
    if (this.isDestroying || this.isDestroyed) return;
    this._invokeCloseModal();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "showSliderClass", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "hideSliderClass", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return CLASS_HIDE;
  }
}), _applyDecoratedDescriptor(_class.prototype, "layerOpened", [action], Object.getOwnPropertyDescriptor(_class.prototype, "layerOpened"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "layerClosed", [action], Object.getOwnPropertyDescriptor(_class.prototype, "layerClosed"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "upTask", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "upTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "closeTask"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbPanelComponent);

export { BbPanelComponent as default };
//# sourceMappingURL=bb-panel.js.map
